import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { StoreProvider } from './contexts/StoreContext';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
// Lazy-loaded routes — split into separate JS chunks so the initial
// Dashboard bundle is dramatically smaller. Auth users only download these
// chunks when they actually navigate to them.
const ClientsPage     = lazy(() => import('./pages/ClientsPage'));
const FabricsPage     = lazy(() => import('./pages/FabricsPage'));
const ClientCardPage  = lazy(() => import('./pages/ClientCardPage'));
const CompanyPage     = lazy(() => import('./pages/CompanyPage'));
const AdminPage       = lazy(() => import('./pages/AdminPage'));
const AdminUsagePage  = lazy(() => import('./pages/AdminUsagePage'));
const AdminApiPage    = lazy(() => import('./pages/AdminApiPage'));
const AdminLeadsPage  = lazy(() => import('./pages/AdminLeadsPage'));
const AdminTemplatesPage = lazy(() => import('./pages/AdminTemplatesPage'));
const SettingsPage    = lazy(() => import('./pages/SettingsPage'));
const AccountPage     = lazy(() => import('./pages/AccountPage'));
const BillingPage     = lazy(() => import('./pages/BillingPage'));
const LandingPage     = lazy(() => import('./pages/LandingPage'));
const PublicDemoPage  = lazy(() => import('./pages/PublicDemoPage'));
const MtmDesignPitchPage = lazy(() => import('./pages/MtmDesignPitchPage'));
const PrivacyPolicyPage  = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const DPAPage            = lazy(() => import('./pages/DPAPage'));
import { Loader2 } from 'lucide-react';
import './App.css';

// Suspense fallback for lazy routes — minimal, matches the brand spinner.
function RouteFallback() {
  return (
    <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#C2A878] spinner" />
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C2A878] spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route - redirect to dashboard if authenticated
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C2A878] spinner" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Master-admin-only gate (used for internal tools)
function MasterAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C2A878] spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const MASTER_EMAIL = 'robson@tailorflow.tech';
  if ((user.email || '').toLowerCase().trim() !== MASTER_EMAIL) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Root: the public marketing homepage is now the live PublicDemoPage —
// interactive replica of the tailor dashboard populated with the master
// admin's real "Robson Abraham" cached looks. Zero credits burned. Regular
// clients still sign in at `/login`; the demo page has a "Sign in" link in
// its top nav for existing users.
function RootLanding() {
  return <PublicDemoPage />;
}

// Public marketing landing page — always renders regardless of auth state.
function PricingLanding() {
  return <LandingPage />;
}

// Welcome page after Stripe Checkout — shows a "Check your email for your login
// link" message. Just re-renders the landing page with a success banner via
// query string so we don't need a whole new route tree.
function WelcomePage() {
  React.useEffect(() => {
    const t = setTimeout(() => {
      window.location.href = '/login?signup=success';
    }, 3500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen bg-[#0A0A0C] text-[#F5F5F5] flex items-center justify-center p-6" data-testid="welcome-page">
      <div className="max-w-md text-center">
        <div className="w-12 h-12 mx-auto mb-6 bg-[#C2A878]/20 border border-[#C2A878] flex items-center justify-center">
          <span className="text-2xl text-[#C2A878]">✓</span>
        </div>
        <h1 className="text-4xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic' }}>
          Welcome to TailorFlow
        </h1>
        <p className="text-[#A1A1AA] mb-6">
          Payment received. Check your email — we've sent your login link + temporary password.
          Redirecting you to sign-in…
        </p>
        <div className="w-8 h-8 mx-auto border-2 border-[#C2A878] border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<RootLanding />} />
        <Route path="/pricing" element={<PricingLanding />} />
        <Route path="/demo" element={<PublicDemoPage />} />
        <Route path="/demo/:tailorName" element={<PublicDemoPage />} />
        <Route path="/mtmdesign" element={<MtmDesignPitchPage />} />
        <Route path="/partners/mtmdesign" element={<MtmDesignPitchPage />} />
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="/login" element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <StoreProvider>
              <Dashboard />
            </StoreProvider>
          </ProtectedRoute>
        } />
        <Route path="/clients" element={
          <ProtectedRoute>
            <StoreProvider>
              <ClientsPage />
            </StoreProvider>
          </ProtectedRoute>
        } />
        <Route path="/fabrics" element={
          <ProtectedRoute>
            <StoreProvider>
              <FabricsPage />
            </StoreProvider>
          </ProtectedRoute>
        } />
        <Route path="/client/:slug" element={<ClientCardPage />} />
        <Route path="/company/:slug" element={<CompanyPage />} />
        {/* Feb 2026 — public legal pages. Linked from every landing
            footer + auth/registration forms. Give TailorFlow a real
            legal posture (privacy, ToS, DPA) without needing a lawyer
            engagement before the first enterprise partnership. */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/dpa" element={<DPAPage />} />
        <Route path="/admin" element={
          <ProtectedRoute>
            <StoreProvider>
              <AdminPage />
            </StoreProvider>
          </ProtectedRoute>
        } />
        <Route path="/admin/usage" element={
          <ProtectedRoute>
            <StoreProvider>
              <AdminUsagePage />
            </StoreProvider>
          </ProtectedRoute>
        } />
        <Route path="/admin/leads" element={
          <ProtectedRoute>
            <StoreProvider>
              <AdminLeadsPage />
            </StoreProvider>
          </ProtectedRoute>
        } />
        <Route path="/admin/api" element={
          <ProtectedRoute>
            <StoreProvider>
              <AdminApiPage />
            </StoreProvider>
          </ProtectedRoute>
        } />
        <Route path="/admin/templates" element={
          <ProtectedRoute>
            <StoreProvider>
              <AdminTemplatesPage />
            </StoreProvider>
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <StoreProvider>
              <SettingsPage />
            </StoreProvider>
          </ProtectedRoute>
        } />
        <Route path="/account" element={
          <ProtectedRoute>
            <StoreProvider>
              <AccountPage />
            </StoreProvider>
          </ProtectedRoute>
        } />
        <Route path="/billing" element={
          <ProtectedRoute>
            <StoreProvider>
              <BillingPage />
            </StoreProvider>
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
